import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getTerritories } from "../../actions/userActions/territoryAction";
import { getCities } from "../../actions/userActions/cityAction";
import LoadingOverlay from "../loadingOverlay/LoadingOverlay";
import SearchInput from "../subComponents/search/SearchInput";
import CustomButton from "../subComponents/buttons/CustomButton";
import CustomPagination from "../subComponents/pagination/CustomPagination";
import ItemsPerPageSelector from "../subComponents/pagination/ItemsPerPageSelector";
import DynamicTable from "../reuse_components/DynamicTable";
import GenericFilter, { FilterBar } from "../subComponents/Filter/GenericFilters";
import CustomFilterSelect from "../designSystem/CustomFilterSelect";
import AddTerritory from "./AddTerritory";
import thIcon from "../../assets/images/thIcon.svg";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { t } from "../../locales/translate";

function Territories() {
  const dispatch = useDispatch();

  // Seed city filter from the logged-in user's city
  const user = JSON.parse(localStorage.getItem("user"));
  const userCityId =
    user?.result?.[0]?.user?.cityId || null;

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [cityFilter, setCityFilter] = useState(userCityId);
  const [statusFilter, setStatusFilter] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [tableData, setTableData] = useState([]);

  const isActiveFilter =
    statusFilter === 2 ? true : statusFilter === 3 ? false : undefined;

  const permissions = JSON.parse(localStorage.getItem("permissionsList"));

  const territories = useSelector(
    (state) => state.territory_reducer.territories
  );
  const isLoading = useSelector(
    (state) => state.territory_reducer.isTerritoryLoading
  );
  const allCities = useSelector((state) => state.cities_reducer.cities);

  const totalPages = territories?.totalPages || 0;
  const totalItems = territories?.totalCount || 0;

  // Fetch cities once for the city filter dropdown
  useEffect(() => {
    dispatch(
      getCities({ filter: { countryId: "1" }, pageNumber: -1, pageSize: 0 })
    );
  }, []);

  // Debounce search query — reset to page 1 on new search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
      if (query !== debouncedQuery) setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [query]);

  // Re-fetch territories when page, page size, city filter, or search changes
  useEffect(() => {
    dispatch(
      getTerritories(
        currentPage,
        itemsPerPage,
        cityFilter || undefined,
        isActiveFilter,
        debouncedQuery || undefined
      )
    );
  }, [currentPage, itemsPerPage, cityFilter, isActiveFilter, debouncedQuery]);

  // Map API data to table rows
  useEffect(() => {
    const data = territories?.result?.map((item) => ({
      ...item,
      cityName: item.city?.cityName || "-",
      status: (
        <span
          className={
            item.isActive ? "status-badge-active" : "status-badge-inactive"
          }
        >
          {item.isActive ? "Active" : "Inactive"}
        </span>
      ),
    }));
    setTableData(data);
  }, [territories]);

  const getCityOptions = () => {
    if (!allCities || allCities === "isLoading" || !allCities.result)
      return [{ id: 0, name: "All" }];
    return [
      { id: 0, name: "All" },
      ...allCities.result
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ id: c.id, name: c.name })),
    ];
  };

  const columns = [
    { label: "Name", accessor: "name" },
    { label: "City", accessor: "cityName" },
    { label: "Status", accessor: "status" },
  ];

  const actions = [
    {
      label: "Edit Territory",
      icon: <EditOutlinedIcon sx={{ color: "#0438B4" }} />,
      permission: "CanUpdateTerritories",
      handler: (row) => {
        setEditData(row);
        setShowModal(true);
      },
    },
  ];

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
    dispatch(getTerritories(1, value, cityFilter || undefined,isActiveFilter,debouncedQuery || undefined)
    );
  };

  const handleCityFilterChange = (e) => {
    setCityFilter(e.target.value || 0);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value || 1);
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setCityFilter(0);
    setStatusFilter(1);
    setCurrentPage(1);
  };

  const toggleFilters = () => setShowFilters((prev) => !prev);

  const handleAddClick = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSaveData = () => {
    dispatch(
      getTerritories(
        currentPage,
        itemsPerPage,
        cityFilter || undefined,
        isActiveFilter,
        debouncedQuery || undefined
      )
    );
    setShowModal(false);
  };

  return (
    <>
      <LoadingOverlay isVisible={isLoading} />
      <div className="main-container">
        <div className="content-wrapper p-0">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 16px" }}>
            <SearchInput
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ minWidth: "80px", marginRight: "10px" }}>
                <ItemsPerPageSelector
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                />
              </div>
              <CustomPagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                totalPages={totalPages}
                onPageChange={(page) => setCurrentPage(page)}
              />
              <div style={{ marginRight: "12px" }}>
                <GenericFilter showFilters={showFilters} toggleFilters={toggleFilters} />
              </div>
              {permissions && permissions.includes("CanAddTerritory") && (
                <CustomButton text={t("addNew")} onClick={handleAddClick} />
              )}
            </div>
          </div>
          <FilterBar showFilters={showFilters} onClearAll={handleClearAllFilters}>
            <CustomFilterSelect
              label={t("status")}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={[
                { id: 1, name: "All" },
                { id: 2, name: "Active" },
                { id: 3, name: "Inactive" },
              ]}
              width="180px"
            />
            <CustomFilterSelect
              label={t("city")}
              value={cityFilter || 0}
              onChange={handleCityFilterChange}
              options={getCityOptions()}
              width="180px"
              searchable={true}
              searchPlaceholder="Search cities..."
              noDataText="No cities available"
            />
          </FilterBar>
        </div>
        <DynamicTable
          data={tableData}
          columns={columns}
          permissions={permissions}
          actions={actions}
          thIcon={thIcon}
          showPagination={totalItems > 0}
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
        {showModal && (
          <AddTerritory
            onClose={handleCloseModal}
            editData={editData}
            onSave={handleSaveData}
          />
        )}
      </div>
    </>
  );
}

export default Territories;
